import React, { useState } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';

// Budget categories
const budgetCategories = [
  "General Requirements",
  "Existing Conditions",
  "03 - Concrete",
  "04 - Masonry",
  "05 - Metals",
  "06 - Wood, Plastic & Composites",
  "07 - Thermal and Moisture Protection",
  "08 - Openings (Doors, Windows, etc)",
  "09 - Finishes",
  "10 - Specialties",
  "12 - Furnishings",
  "13 - Special Construction",
  "14 - Conveying Equipment",
  "21 - Fire Suppression",
  "22 - Plumbing",
  "23 - HVAC",
  "26 - Electrical",
  "28 - Electronic Safety / Security",
  "98 - General Conditions",
  "99 - Contingency",
];

// Budget columns
const budgetColumns = [
  "Original Budget",
  "Approved Budget Out",
  "Approved Budget In",
  "Budget Mod.",
  "Revised Budget",
  "Pending Budget In",
  "Projected Budget",
  "Committed Costs",
  "Pending Cost In",
  "Direct Costs",
  "Projected Costs",
  "Forecast to Complete",
  "Estimated Cost at Completion",
  "Projected Over-Under",
  "Pending Budget Out",
  "Pending Cost Out",
  "Job-to-Date Cost",
];

// Mock data
const mockBudgetData = budgetCategories.map((category) => {
  const [code, ...descParts] = category.split(" - ");
  const data = {
    category,
    code: code || category,
    description: descParts.length ? descParts.join(" - ") : "",
  };
  budgetColumns.forEach((col) => {
    if (col === "Projected Over-Under") {
      data[col] = `$${(Math.random() * 200000 - 100000).toFixed(2)}`;
    } else {
      data[col] = `$${(Math.random() * 100000).toFixed(2)}`;
    }
  });
  return data;
});

// Category groups
const categoryGroups = {
  "Materials": ["03 - Concrete", "04 - Masonry", "05 - Metals", "06 - Wood, Plastic & Composites", "07 - Thermal and Moisture Protection", "08 - Openings (Doors, Windows, etc)", "09 - Finishes"],
  "Labor": ["General Requirements", "Existing Conditions"],
  "Permits/Fees": ["98 - General Conditions", "99 - Contingency"],
  "Mechanical/Electrical": ["21 - Fire Suppression", "22 - Plumbing", "23 - HVAC", "26 - Electrical", "28 - Electronic Safety / Security"],
  "Specialties": ["10 - Specialties", "12 - Furnishings", "13 - Special Construction", "14 - Conveying Equipment"],
};

const groupDataByCategory = (data, column) => {
  const groups = {};
  Object.entries(categoryGroups).forEach(([group, categories]) => {
    const total = data
      .filter((item) => categories.includes(item.category))
      .reduce((sum, item) => sum + parseFloat(item[column].replace("$", "")), 0);
    groups[group] = parseFloat(total.toFixed(2));
  });
  return groups;
};

// Pie chart colors
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// Color coding for "Projected Over-Under" column
const getColorClass = (value) => {
  const numValue = parseFloat(value.replace("$", ""));
  if (numValue > 0) return "text-success"; // Green = positive
  if (numValue < 0) return "text-error"; // Red = negative
  return ""; // Neutral = zero
};

// Pie chart label renderer
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }) => {
  const radius = outerRadius * 1.2;
  const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
  const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));
  const formattedValue = (value / 1000).toFixed(0) + 'K'; // ("69K, 420K, etc.")

  return (
    <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
      {`$${formattedValue}`}
    </text>
  );
};

const BudgetPage = () => {
  const [searchTerm, setSearchTerm] = useState("");

  // Filter data with search
  const filteredData = mockBudgetData.filter((item) =>
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Summary data
  const totalBudget = mockBudgetData.reduce((sum, item) => sum + parseFloat(item["Original Budget"].replace("$", "")), 0);
  const totalSpent = mockBudgetData.reduce((sum, item) => sum + parseFloat(item["Job-to-Date Cost"].replace("$", "")), 0);
  const remainingBudget = totalBudget - totalSpent;
  const totalProjectedOverUnder = mockBudgetData.reduce((sum, item) => sum + parseFloat(item["Projected Over-Under"].replace("$", "")), 0);

  // Cost breakdown data for pie chart
  const costBreakdownData = Object.entries(groupDataByCategory(mockBudgetData, "Job-to-Date Cost")).map(([name, value]) => ({ name, value }));

  return (
    <div className="flex flex-col h-screen bg-base-200 text-white overflow-y-auto">
      <div className="navbar bg-base-100 shadow-lg sticky top-0 z-10">
        <div className="navbar-start">
          <a href="/project-management" className="btn btn-ghost text-xl p-0">
            UrbanTectonic
          </a>
        </div>
        <div className="navbar-end">
          <div className="form-control">
            <input
              type="text"
              placeholder="Search categories..."
              className="input input-bordered input-sm w-40 md:w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="btn btn-ghost btn-circle ml-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="container mx-auto p-6 flex-1">
        <h1 className="text-3xl font-semibold mb-6">Budget Overview</h1>

        {/* Budget Summary */}
        <div className="card bg-base-100 shadow-xl mb-6">
          <div className="card-body">
            <h2 className="card-title">Budget Summary</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm">Total Budget</p>
                <p className="text-2xl font-bold">${totalBudget.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm">Total Spent</p>
                <p className="text-2xl font-bold">${totalSpent.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm">Remaining Budget</p>
                <p className="text-2xl font-bold">${remainingBudget.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Status */}
        <div className={`alert ${totalProjectedOverUnder >= 0 ? 'alert-success' : 'alert-error'} shadow-lg mb-6`}>
          <div>
            <span>Projected Budget Status: </span>
            {totalProjectedOverUnder >= 0 ? (
              <span>Under Budget by ${totalProjectedOverUnder.toLocaleString()}</span>
            ) : (
              <span>Over Budget by ${Math.abs(totalProjectedOverUnder).toLocaleString()}</span>
            )}
          </div>
        </div>

        {/* Pie Chart */}
        <div className="card bg-base-100 shadow-xl mb-6">
          <div className="card-body flex flex-col items-center">
            <h2 className="card-title">Cost Breakdown by Category</h2>
            <div className="flex justify-center">
              <PieChart width={500} height={500}>
                <Pie
                  data={costBreakdownData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={150}
                  fill="#8884d8"
                  label={renderCustomizedLabel}
                  labelLine={true}
                >
                  {costBreakdownData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                <Legend />
              </PieChart>
            </div>
          </div>
        </div>

        {/* Budget Table */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body p-0">
            <div className="overflow-x-auto">
              <table className="table w-full border-collapse">
                {/* Header */}
                <thead className="bg-gray-900 text-white sticky top-0 z-10">
                  <tr>
                    <th className="p-4 w-12 text-center rounded-tl-lg border-r border-gray-500">Code</th>
                    <th className="p-4 text-left border-r border-gray-500">Description</th>
                    {budgetColumns.map((col, idx) => (
                      <th key={idx} className="p-4 text-right border-r border-gray-500 last:border-r-0">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                {/* Body */}
                <tbody>
                  {filteredData.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-800 transition-colors duration-200"
                    >
                      <td className="p-4 text-center border-b border-gray-700 font-mono text-sm border-r border-gray-500">
                        {item.code}
                      </td>
                      <td className="p-4 border-b border-gray-700 font-semibold border-r border-gray-500">
                        {item.description || item.category}
                      </td>
                      {budgetColumns.map((col, idx) => (
                        <td
                          key={idx}
                          className={`p-4 text-right border-b border-gray-700 border-r border-gray-500 last:border-r-0 ${
                            col === "Projected Over-Under" ? getColorClass(item[col]) : ""
                          }`}
                        >
                          {item[col]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer footer-center p-4 bg-base-100 text-base-content">
        <div>
          <p>UrbanTectonic - Simplifying Home Building</p>
        </div>
      </footer>
    </div>
  );
};

export default BudgetPage;
